import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from './Context/UserContext';
import Sidebar from './Sidebar';
import UserInfoCard from './UserInfoCard';
import '../styles/AppShell.css';

/* ────────────────────────────────────────────────────────────────────────────
   Live greeting + clock. Isolated so the per-second tick only re-renders this
   small block — never the page content (charts, tables, …) below it.
   ──────────────────────────────────────────────────────────────────────────── */
function TopbarGreeting({ user, eyebrow, title, titleAccent }) {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    const hour = now.getHours();
    const greetingWord =
        hour < 12 ? 'Good morning,' : hour < 17 ? 'Good afternoon,' : 'Good evening,';
    const dateStr = now.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    // Vendors are greeted by their name/company; internal users by their role.
    const isVendor = Array.isArray(user?.userTypeIds) && user.userTypeIds.includes(25);
    const personName =
        user?.NameOfCompany || user?.nameOfCompany ||
        user?.vendorName || user?.VendorName ||
        user?.contactPersonName || user?.contactPerson || user?.name;
    const accent =
        titleAccent ||
        (isVendor
            ? (personName || 'Vendor')
            : (user?.roles?.length
                ? user.roles.map(r => r.role).join(' / ')
                : (personName || 'there')));

    return (
        <div className="app-topbar-titles">
            <span className="app-eyebrow app-clock">
                {eyebrow || (
                    <>
                        {dateStr}
                        <span className="app-clock-sep" aria-hidden="true">·</span>
                        <span className="app-clock-time">{timeStr}</span>
                    </>
                )}
            </span>
            <h1 className="app-title">
                {title || greetingWord}{' '}
                <span className="app-title-accent">{title ? titleAccent : accent}</span>
            </h1>
        </div>
    );
}

/* ────────────────────────────────────────────────────────────────────────────
   Command palette — Ctrl-K / ⌘K. Searches the screens this user actually has
   (their own `menus`), so it can never navigate somewhere they lack access to.
   Full keyboard control: ↑ ↓ to move, Enter to open, Esc to dismiss.
   ──────────────────────────────────────────────────────────────────────────── */
function CommandPalette({ items, onClose }) {
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const navigate = useNavigate();

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items.slice(0, 12);
        return items.filter(i => i.label.toLowerCase().includes(q)).slice(0, 12);
    }, [query, items]);

    useEffect(() => { setActive(0); }, [query]);

    useEffect(() => {
        const previouslyFocused = document.activeElement;
        inputRef.current?.focus();
        return () => {
            if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
        };
    }, []);

    // Keep the highlighted row in view when arrowing past the fold.
    useEffect(() => {
        const node = listRef.current?.querySelector('.cmdk-item.is-active');
        node?.scrollIntoView({ block: 'nearest' });
    }, [active]);

    const go = useCallback((item) => {
        if (!item) return;
        onClose();
        navigate(item.to);
    }, [navigate, onClose]);

    const onKeyDown = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); onClose(); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); go(results[active]); }
    };

    return (
        <div
            className="cmdk-scrim"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="cmdk" role="dialog" aria-modal="true" aria-label="Search screens and actions">
                <div className="cmdk-input-row">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                        <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        ref={inputRef}
                        className="cmdk-input"
                        placeholder="Search screens…"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onKeyDown={onKeyDown}
                        aria-label="Search screens"
                        aria-controls="cmdk-results"
                        aria-activedescendant={results[active] ? `cmdk-opt-${active}` : undefined}
                        autoComplete="off"
                        spellCheck="false"
                    />
                </div>

                {results.length === 0 ? (
                    <p className="cmdk-empty">
                        Nothing matches &ldquo;{query}&rdquo;. Try a shorter word.
                    </p>
                ) : (
                    <ul className="cmdk-list" id="cmdk-results" role="listbox" ref={listRef}>
                        {results.map((item, i) => (
                            <li key={item.to} role="option" id={`cmdk-opt-${i}`} aria-selected={i === active}>
                                <button
                                    type="button"
                                    className={`cmdk-item${i === active ? ' is-active' : ''}`}
                                    onMouseEnter={() => setActive(i)}
                                    onClick={() => go(item)}
                                >
                                    <span className="cmdk-item-icon" aria-hidden="true">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                        </svg>
                                    </span>
                                    <span className="cmdk-item-label">{item.label}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="cmdk-foot">
                    <span><kbd>↑</kbd> <kbd>↓</kbd> to move</span>
                    <span><kbd>Enter</kbd> to open</span>
                    <span><kbd>Esc</kbd> to close</span>
                </div>
            </div>
        </div>
    );
}

/* ────────────────────────────────────────────────────────────────────────────
   AppLayout — the shell every screen renders inside.
   ──────────────────────────────────────────────────────────────────────────── */
export default function AppLayout({ eyebrow, title, titleAccent, children }) {
    const { user } = useUser();
    const location = useLocation();
    const [showCard, setShowCard] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem('vms_nav_collapsed') === 'true'; } catch { return false; }
    });
    const [dark, setDark] = useState(() => {
        try {
            const stored = localStorage.getItem('darkMode');
            if (stored !== null) return stored === 'true';
        } catch { /* storage unavailable */ }
        return typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-color-scheme: dark)').matches;
    });

    // On the dashboard show a "Home" link; elsewhere show a "Dashboard" link.
    const onDashboard = location.pathname.toLowerCase() === '/master-page';

    useEffect(() => {
        document.body.classList.toggle('dark-mode', dark);
        document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
        try { localStorage.setItem('darkMode', dark); } catch { /* ignore */ }
        // Let other views (e.g. the charts dashboard) re-theme in sync.
        window.dispatchEvent(new CustomEvent('vms:darkmode', { detail: dark }));
    }, [dark]);

    useEffect(() => {
        try { localStorage.setItem('vms_nav_collapsed', collapsed); } catch { /* ignore */ }
    }, [collapsed]);

    // Remove the global-nav body offset — this shell owns the full viewport.
    useEffect(() => {
        document.body.classList.add('no-global-nav');
        return () => document.body.classList.remove('no-global-nav');
    }, []);

    // Close the mobile drawer on navigation so it never covers the new page.
    useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

    // Ctrl-K / ⌘K opens the palette from anywhere. Esc closes the account menu.
    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setPaletteOpen(o => !o);
            } else if (e.key === 'Escape') {
                setShowCard(false);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    // Palette entries come from this user's own menus — never a hardcoded list.
    const paletteItems = useMemo(() => {
        const seen = new Set();
        return (user?.menus || [])
            .filter(m => m.MenuLink && m.MenuName)
            .filter(m => (seen.has(m.MenuLink) ? false : seen.add(m.MenuLink)))
            .map(m => ({ label: m.MenuName, to: m.MenuLink }));
    }, [user]);

    return (
        <div className={`app-shell${collapsed ? ' is-collapsed' : ''}${drawerOpen ? ' is-drawer-open' : ''}`}>
            <Sidebar
                collapsed={collapsed}
                onToggleCollapse={() => setCollapsed(c => !c)}
                onNavigate={() => setDrawerOpen(false)}
            />
            {drawerOpen && (
                <div
                    className="app-drawer-scrim"
                    onClick={() => setDrawerOpen(false)}
                    aria-hidden="true"
                />
            )}

            <main className="app-main">
                <header className="app-topbar">
                    <button
                        type="button"
                        className="app-ctl-menu"
                        onClick={() => setDrawerOpen(o => !o)}
                        aria-label="Open navigation"
                        aria-expanded={drawerOpen}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
                        </svg>
                    </button>

                    <TopbarGreeting user={user} eyebrow={eyebrow} title={title} titleAccent={titleAccent} />

                    <div className="app-topbar-controls">
                        <button
                            type="button"
                            className="app-ctl-search"
                            onClick={() => setPaletteOpen(true)}
                            aria-label="Search screens"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                                <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <span className="app-ctl-search-label">Search</span>
                            <span className="app-ctl-kbd" aria-hidden="true">Ctrl K</span>
                        </button>

                        {onDashboard ? (
                            <Link to="/" className="app-ctl-nav-btn">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                                Home
                            </Link>
                        ) : (
                            <Link to="/master-page" className="app-ctl-nav-btn">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /></svg>
                                Dashboard
                            </Link>
                        )}

                        <button
                            type="button"
                            className="app-ctl-refresh"
                            onClick={() => window.location.reload()}
                            title="Reload this page"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                            </svg>
                            <span className="app-refresh-label">Refresh</span>
                        </button>

                        <button
                            type="button"
                            className={`app-ctl-theme ${dark ? 'is-dark' : ''}`}
                            onClick={() => setDark(d => !d)}
                            aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
                            aria-pressed={dark}
                            title={dark ? 'Light mode' : 'Dark mode'}
                        >
                            <span className="app-ctl-theme-icon app-ctl-theme-icon-sun" aria-hidden="true">☀</span>
                            <span className="app-ctl-theme-icon app-ctl-theme-icon-moon" aria-hidden="true">☾</span>
                            <span className="app-ctl-theme-knob" />
                        </button>

                        {user && (
                            <div className="app-ctl-account">
                                <button
                                    type="button"
                                    className={`app-ctl-avatar-btn ${showCard ? 'is-active' : ''}`}
                                    onClick={() => setShowCard(s => !s)}
                                    aria-label="Account menu"
                                    aria-expanded={showCard}
                                >
                                    <span className="app-ctl-avatar app-ctl-avatar-icon" aria-hidden="true">
                                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                    </span>
                                </button>
                                {showCard && (
                                    <UserInfoCard user={user} onClose={() => setShowCard(false)} />
                                )}
                            </div>
                        )}
                    </div>
                </header>

                <div className="app-content">
                    {children}
                </div>
            </main>

            {paletteOpen && (
                <CommandPalette items={paletteItems} onClose={() => setPaletteOpen(false)} />
            )}
        </div>
    );
}
